import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { PiBracketsCurly } from 'react-icons/pi';
import React, { useRef, useState } from 'react';
import {
  DrawGeometryType,
  propagateGeometryChanges,
} from '@ais-ui/react-openlayers';
import { UseFormReturn } from 'react-hook-form';
import {
  circleSchema,
  createGeometryFromGeoJson,
  geometrySchema,
  lineStringSchema,
  pointSchema,
  polygonSchema,
} from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { SafeParseReturnType, SafeParseSuccess, ZodError } from 'zod';

interface TextGeometryPopoverProps {
  form: UseFormReturn<any>;
  geometryType: DrawGeometryType;
  shape: GeoJson;
}

const isGeojsonValid = (
  geometryType: DrawGeometryType,
  newGeometry: Record<string, any>,
  radiusValue: number | undefined
): boolean => {
  let validationResult;
  switch (geometryType) {
    case 'Circle':
      validationResult = circleSchema.safeParse({
        ...newGeometry,
        radius: radiusValue,
      });
      break;
    case 'Point':
      validationResult = pointSchema.safeParse(newGeometry);
      break;
    case 'LineString':
      validationResult = lineStringSchema.safeParse(newGeometry);
      break;
    case 'Polygon':
      validationResult = polygonSchema.safeParse(newGeometry);
      break;
    default:
      validationResult = {
        success: false,
        error: new Error('Unknown geometry type'),
      };
  }

  return validationResult.success;
};

export const TextGeometryPopover = ({
  form,
  geometryType,
  shape,
}: TextGeometryPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { messages } = useLocalization();
  const radiusInputRef = useRef<HTMLInputElement>(null);
  const geojsonTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  const onSubmitGeometry = () => {
    try {
      const radiusValue = radiusInputRef.current?.value
        ? Number(radiusInputRef.current.value)
        : undefined;
      const geojsonString = geojsonTextareaRef.current?.value || '';

      const newGeometry = JSON.parse(geojsonString);

      const isValid = isGeojsonValid(geometryType, newGeometry, radiusValue);

      if (!isValid) {
        console.error('Invalid GeoJSON structure:');
        toast({
          title: messages.genericToastTitleError,
          description:
            'Invalid GeoJSON structure. Please check coordinates and geometry type.',
          variant: 'destructive',
        });
        return;
      }

      // Create the shape with radius if it's a circle
      const newShape = {
        ...shape,
        geometry: newGeometry,
        properties:
          geometryType === 'Circle'
            ? { ...shape.properties, radius: radiusValue }
            : shape.properties,
      };

      const geometry = createGeometryFromGeoJson(newShape);

      propagateGeometryChanges(geometry, 'geometry', form);

      setIsOpen(false);
    } catch (error) {
      console.error('Error processing GeoJSON:', error);
      toast({
        title: messages.genericToastTitleError,
        description: messages.shapesTextGeojsonError,
        variant: 'destructive',
      });
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10"
            >
              <PiBracketsCurly className="h-8 w-8"></PiBracketsCurly>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{messages.textShapePopoverText}</TooltipContent>
      </Tooltip>
      <PopoverContent side="left" className="w-96 h-96">
        <div className="flex flex-col w-full h-full space-y-4">
          {geometryType === 'Circle' && (
            <div className="flex flex-col space-y-2">
              <Label htmlFor="radius">Radius</Label>
              <Input
                id="radius"
                type="number"
                ref={radiusInputRef}
                defaultValue={
                  shape?.properties?.radius ? shape.properties.radius : 0
                }
              />
            </div>
          )}
          <div className="h-full flex flex-col space-y-2">
            <Label htmlFor="geojson">GeoJson</Label>
            <Textarea
              className="h-full"
              placeholder="Enter your geojson here"
              id="geojson"
              ref={geojsonTextareaRef}
              defaultValue={
                shape?.geometry ? JSON.stringify(shape.geometry, null, 2) : ''
              }
            ></Textarea>
          </div>
          <Button onClick={onSubmitGeometry}>Submit</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
